import { type GridColDef } from '@mui/x-data-grid-pro';
import { Tooltip, Link, Box } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import type { CellContentProps, ColumnOptions, WithID } from './types';

/**
 * Wraps a value to display it in a cell.
 */
const CellContent = ({ tooltip, link, value, extra, formatValue, width, style, ignoreValue }: CellContentProps) => {
    const formatted = typeof formatValue === 'function' ? formatValue(value) : value;

    return (
        <Box
            height='100%'
            width={width}
            display='flex'
            gap='10px'
            alignItems='center'
            sx={{
                whiteSpace: 'break-spaces',
                hyphens: 'auto',
                ...style,
            }}>
            {!ignoreValue && (
                <Tooltip title={tooltip} placement='top'>
                    {!formatted ? (
                        <>-</>
                    ) : link ? (
                        <Link component={RouterLink} to={link} color='inherit'>
                            {formatted}
                        </Link>
                    ) : (
                        <>{formatted}</>
                    )}
                </Tooltip>
            )}

            {extra}
        </Box>
    );
};

/**
 * Generate a {@link GridColDef} that can be passed to MUI {@link DataGrid}.
 */
export const createColumn = <Row extends Record<string, any>>({
    getCellLink,
    renderExtraCellContent,
    formatValue,
    tooltip,
    width,
    getStyles,
    ignoreValue,
    ...rest
}: ColumnOptions<Row>): GridColDef<Row> => {
    return {
        // Defaults
        sortable: false,
        ...(width
            ? {
                  width,
              }
            : {
                  // ? Allows for responsive cells
                  flex: 1,
              }),
        renderCell: ({ row, value }) => {
            return (
                <CellContent
                    style={getStyles?.(row)}
                    width={width}
                    value={value}
                    formatValue={formatValue}
                    tooltip={tooltip}
                    link={getCellLink?.(row as WithID<Row>)}
                    extra={renderExtraCellContent?.(row as WithID<Row>)}
                    ignoreValue={ignoreValue}
                />
            );
        },
        // Overrides
        ...rest,
    };
};
